import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import Keyboard from 'react-simple-keyboard'
import 'react-simple-keyboard/build/css/index.css'
import { useTranslation } from 'react-i18next'
import useIdleTimer from '../hooks/useIdleTimer'

import { db } from '../firebase/config'
import { collection, addDoc, serverTimestamp } from 'firebase/firestore'
import { enqueuePendingScore, flushPendingScores, generateScoreClientId } from '../utils/offlineScores'

const sanitizeInitial = (value) => {
  const cleaned = (value || '')
    .toString()
    // Allow basic latin + latin-1 supplement letters (covers French accents like É, Ç, À, Ù, etc.)
    .replace(/[^A-Za-zÀ-ÖØ-öø-ÿ]/g, '')
  return cleaned.toUpperCase().slice(0, 1)
}

const keyboardLayouts = {
  'default-en': [
    '1 2 3 4 5 6 7 8 9 0 - {bksp}',
    'q w e r t y u i o p',
    'a s d f g h j k l',
    '{shift} z x c v b n m , . {shift}',
    '.com @ {space} .ca',
  ],
  'shift-en': [
    '! @ # $ ^ & * ( ) _ + {bksp}',
    'Q W E R T Y U I O P',
    'A S D F G H J K L',
    '{shift} Z X C V B N M ? . {shift}',
    '.com @ {space} .ca',
  ],
  'default-fr': [
    '1 2 3 4 5 6 7 8 9 0 - {bksp}',
    'q w e r t y u i o p ç .',
    'a s d f g h j k l ; è à',
    '{shift} ù z x c v b n m , é {shift}',
    '.com @ {space} .ca',
  ],
  'shift-fr': [
    '! @ # $ % & * ( ) _ + {bksp}',
    "Q W E R T Y U I O P Ç '",
    'A S D F G H J K L È À "',
    '{shift} Ù Z X C V B N M , É {shift}',
    '.com @ {space} .ca',
  ],
  numbers: ['1 2 3', '4 5 6', '7 8 9', '0 {bksp}'],
}

const DataEntry = () => {
  const [keyboardIns, setKeyboardIns] = useState(null)
  const [focusedInput, setFocusedInput] = useState('')
  const [maxLength, setMaxLength] = useState(99)
  const [loading, setLoading] = useState(false)
  const firstInitialRef = useRef(null)
  const lastInitialRef = useRef(null)
  const isKiosk = typeof window !== 'undefined' && Boolean(window.localStorage?.getItem('kiosk-id'))

  const { i18n, t } = useTranslation()
  const lang = (i18n.language || 'en').toLowerCase().startsWith('fr') ? 'fr' : 'en'
  const [keyLayoutName, setKeyLayoutName] = useState('default-' + lang)

  const [formData, setFormData] = useState({ firstName: '', lastName: '' })

  const navigate = useNavigate()

  // const { current } = useSelector((state) => state.level)

  useEffect(() => {
    setKeyLayoutName('default-' + lang)
  }, [lang])

  const onKeyboardInit = (keyboard) => {
    setKeyboardIns(keyboard)
  }

  const setValueToInput = (value) => {
    const updated = { ...formData }

    switch (focusedInput) {
      case 'firstName':
        updated[focusedInput] = sanitizeInitial(value)
        if (updated[focusedInput].length === 1) {
          setTimeout(() => {
            lastInitialRef.current?.focus()
          }, 0)
        }
        break
      case 'lastName':
        {
          const was = formData.lastName
          updated[focusedInput] = sanitizeInitial(value)
          // If we just erased the last initial, jump back to first initial (typical behavior)
          if (was && !updated[focusedInput]) {
            setTimeout(() => {
              firstInitialRef.current?.focus()
            }, 0)
          }
        }
        break
      case 'company':
        updated[focusedInput] = value ? value.charAt(0).toUpperCase() + value.slice(1) : ''
        break
      default:
        updated[focusedInput] = value
        break
    }

    setFormData((prevState) => ({
      ...prevState,
      ...updated,
    }))
  }

  const onChange = (value) => {
    setValueToInput(value)
  }

  const onKeyPress = (button) => {
    // If last initial is already empty, backspace should jump to the first initial
    if (button === '{bksp}' && focusedInput === 'lastName' && !formData.lastName) {
      setTimeout(() => {
        firstInitialRef.current?.focus()
      }, 0)
      return
    }

    if (button === '{shift}' || button === '{lock}') {
      handleShift()
    } else if (button === '{numbers}' || button === '{abc}') {
      handleNumbers()
    }

    let currentInputValue = ''
    switch (focusedInput) {
      case 'firstName':
        currentInputValue = formData.firstName
        break
      case 'lastName':
        currentInputValue = formData.lastName
        break
      default:
        break
    }

    let specialValue = null
    switch (button) {
      case '{@}':
        specialValue = currentInputValue + '@'
        break
      case '{.}':
        specialValue = currentInputValue + '.'
        break
      case '{-}':
        specialValue = currentInputValue + '-'
        break
      case '{_}':
        specialValue = currentInputValue + '_'
        break
      case '{.com}':
        specialValue = currentInputValue + '.com'
        break
      case '{#}':
        specialValue = currentInputValue + '#'
        break
      case '{+}':
        specialValue = currentInputValue + '+'
        break
      default:
        break
    }

    if (specialValue) {
      setValueToInput(specialValue)
    }
  }

  const handleShift = () => {
    if (keyLayoutName === 'numbers') return

    const newLayoutName = keyLayoutName.startsWith('default') ? 'shift-' + lang : 'default-' + lang

    setKeyLayoutName(newLayoutName)
  }

  const handleNumbers = () => {
    const currentLayout = keyLayoutName
    const numbersToggle = currentLayout !== 'numbers' ? 'numbers' : 'default-' + lang

    setKeyLayoutName(numbersToggle)
  }

  const onInputFocus = (inputName, input) => {
    const inputValue = input.value
    const maxLength = Number(input.getAttribute('maxlength')) || 99
    const type = input.getAttribute('type')

    if (keyboardIns !== null) {
      keyboardIns.setInput(inputValue)
    }

    setMaxLength(maxLength)
    setKeyLayoutName(type === 'number' ? 'numbers' : 'default-' + lang)
    setFocusedInput(inputName)
  }

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault()

      if (!formData.firstName || !formData.lastName) {
        return
      }

      setLoading(true)

      // Store form data in localStorage
      const firstName = formData.firstName
      const lastName = formData.lastName
      const initials = `${firstName}${lastName}`
      localStorage.setItem('firstName', firstName)
      localStorage.setItem('lastName', lastName)
      localStorage.setItem('initials', initials)
      const language = i18n.language
      localStorage.setItem('language', language)

      const storedScore = localStorage.getItem('lastGameScore')
      const parsedScore = parseInt(storedScore || '', 10)
      const score = Number.isFinite(parsedScore) ? parsedScore : 0
      const kioskId = localStorage.getItem('kiosk-id') || ''
      const createdAt = new Date().toISOString()

      const payload = {
        score,
        firstName,
        lastName,
        initials,
        language,
        kioskId,
        createdAt,
        clientId: generateScoreClientId(),
      }

      localStorage.setItem('lastScoreClientId', payload.clientId)

      const canAttemptOnline = typeof navigator === 'undefined' || navigator.onLine

      if (!canAttemptOnline) {
        enqueuePendingScore(payload)
      } else {
        try {
          const scoresRef = collection(db, 'scores')
          await addDoc(scoresRef, {
            ...payload,
            timestamp: serverTimestamp(),
            syncedAt: serverTimestamp(),
          })
          await flushPendingScores()
        } catch (error) {
          console.error('Error saving score to Firebase:', error)
          enqueuePendingScore(payload)
        }
      }

      navigate('/thank-you');
      setLoading(false);
    },
    [navigate, formData, i18n.language]
  )

  useIdleTimer()

  return (
    <>
      <h2 className="text-[66px] leading-[1.05] mt-[210px] w-[620px] mx-auto text-white font-euclid-circular-b-bold text-left">
        {t('dataEntry')}
      </h2>

      {/* <div className="text-black text-center bg-gray-100 w-[690px] h-[150px] rounded mx-auto mt-10 flex justify-center items-center">
        <p className="text-[80px]">{formatTime(score)}</p>
      </div> */}

      <div className="flex flex-col justify-center mx-auto items-center">
        {/* Name inputs side by side */}
        <div className="flex gap-[25px] w-full mt-[30px] mb-[20px]">
          <div className="text-black w-[131px] h-[150px]">
            <input
              ref={firstInitialRef}
              type="text"
              value={formData.firstName}
              placeholder=""
              maxLength="1"
              onChange={(e) => setValueToInput(e.target.value)}
              onFocus={(e) => onInputFocus('firstName', e.target)}
              readOnly={isKiosk}
              inputMode={isKiosk ? 'none' : undefined}
              onPaste={isKiosk ? (e) => e.preventDefault() : undefined}
              autoFocus
              autoComplete="off"
              spellCheck={false}
              required
              className="text-[40px] text-center w-full h-full py-4 px-8  rounded-lg mx-auto shadow-[inset_-5.663px_-4.114px_3px_rgba(255,255,255,0.04),inset_5.516px_4.31px_3px_rgba(0,0,0,0.09)] focus:outline-[#111]"
            />
          </div>

          <div className="text-black w-[131px] h-[150px]">
            <input
              ref={lastInitialRef}
              type="text"
              value={formData.lastName}
              placeholder=""
              maxLength="1"
              onChange={(e) => setValueToInput(e.target.value)}
              onFocus={(e) => onInputFocus('lastName', e.target)}
              readOnly={isKiosk}
              inputMode={isKiosk ? 'none' : undefined}
              onPaste={isKiosk ? (e) => e.preventDefault() : undefined}
              autoComplete="off"
              spellCheck={false}
              required
              className="text-[40px] text-center w-full h-full py-4 px-8 rounded-lg mx-auto shadow-[inset_-5.663px_-4.114px_3px_rgba(255,255,255,0.04),inset_5.516px_4.31px_3px_rgba(0,0,0,0.09)] focus:outline-[#111]"
            />
          </div>
        </div>

      </div>

      {isKiosk && (
        <div className="mt-[20px]">
          <div className="text-2xl font-bold min-h-[360px]">
            <Keyboard
              layoutName={keyLayoutName}
              onChange={onChange}
              disableButtonHold={true}
              onKeyPress={onKeyPress}
              onInit={onKeyboardInit}
              stopMouseUpPropagation={true}
              stopMouseDownPropagation={true}
              layout={keyboardLayouts}
              maxLength={maxLength}
              display={{
                '{bksp}': '⌫',
                '{shift}': '⇧',
                '{controlleft}': 'ctrl ⌃',
                '{controlright}': 'ctrl ⌃',
                '{@}': '@',
                '{-}': '-',
                '{_}': '_',
                '{.}': '.',
                '{.com}': '.com',
                '{space}': ' ',
                '{return}': 'return',
              }}
            />
          </div>
        </div>
      )}

      <div className="">
        <button
          disabled={loading}
          type="submit"
          className="bg-[#ffab54] absolute bottom-[150px] left-1/2 -translate-x-1/2 w-[379px] h-[93px] flex items-center justify-center cursor-pointer rounded-[10px] disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={onSubmit}
        >
          <span className="relative z-10 text-[#000b48] text-[48px] font-euclid-circular-b-semibold">{t('submit')}</span>
        </button>
      </div>
    </>
  )
}

export default DataEntry
